import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { useAnchorPriceGroupName } from '../../../shared/useAnchorPriceGroupName';
import NumberInput from '../../../shared/NumberInput';
import { formatMoney as formatMoneyShared, formatNumber, formatPercent, getTenantHomeCurrency } from '../../../shared/numberFormat';

function productLabel(p) {
  return `${p.sku} — ${p.name}`;
}

// '—' for blank; `currencyCode` marks the Supplier's currency (none = the
// home currency, e.g. the Dealer prices).
function formatMoney(value, currencyCode) {
  return formatMoneyShared(value, { currency: currencyCode || undefined }) || '—';
}

function perBaseUnit(price, conversionRate) {
  if (price === null || price === undefined) return null;
  if (!conversionRate) return null;
  return price / conversionRate;
}

// Where a Dealer Price actually came from — the Price Adjustment that won
// resolution for this Product/UoM/Customer Price Group, or (when nothing
// won) the plain Retail Price it fell back to — shown alongside its row
// label rather than under the dollar figure, since it's explaining the
// label, not the number.
function DealerPriceSource({ priceAdjustmentId, productId }) {
  const anchorName = useAnchorPriceGroupName();
  if (!priceAdjustmentId) {
    return (
      <>
        {' '}
        <Link
          to={`/sales-prices?product_id=${productId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11 }}
          title="Open this Product's Prices"
        >
          (via {anchorName} Price)
        </Link>
      </>
    );
  }
  return (
    <>
      {' '}
      <Link
        to={`/sales-prices/${priceAdjustmentId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 11 }}
        title={`Open the Price (#${priceAdjustmentId}) this Dealer Price resolved from`}
      >
        (via Price)
      </Link>
    </>
  );
}

// Tag one SKU's UoM against a Rule, then enter the Supplier's actual quoted
// Fixed Amount over Optim's live-calculated figure — same calculated-vs-
// entered shape as a manually-entered Reference Price (see
// docs/product/modules/Purchasing/vis_supplier_rebates.md). The UoM picked
// here is expected to be the Rule's own UoM2 — the one that earns the
// rebate — Optim can't verify that at entry time, staff pick the row that
// matches what the Rule actually names.
function SupplierRebateSkuRateForm({ initial, onSave, onCancel, loading }) {
  const [productId, setProductId] = useState(initial?.product_uom?.product_id ? String(initial.product_uom.product_id) : '');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductResults, setShowProductResults] = useState(false);
  const skipNextProductSearch = useRef(true);

  const [productUoms, setProductUoms] = useState([]);
  const [productUomId, setProductUomId] = useState(initial?.product_uom_id ? String(initial.product_uom_id) : '');

  const [rules, setRules] = useState([]);
  const [ruleId, setRuleId] = useState(initial?.rule_id ? String(initial.rule_id) : '');

  const [calculated, setCalculated] = useState(null);
  const [fixedAmount, setFixedAmount] = useState(initial?.fixed_amount !== undefined && initial?.fixed_amount !== null ? String(Number(initial.fixed_amount)) : '');
  const [amountEdited, setAmountEdited] = useState(!!initial);

  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/supplier-rebate-rules', { params: { status: 'active' } }).then((res) => setRules(res.data.data || [])).catch(() => setRules([]));
  }, []);

  // Prefill for an existing row: resolve its Product from the ProductUom
  // relation the list endpoint already includes, so Edit opens with the
  // same Product context Add would have built up interactively.
  useEffect(() => {
    if (!initial?.product_uom?.product_id) return;
    const productIdFromRow = String(initial.product_uom.product_id);
    setProductId(productIdFromRow);
    axios.get(`/api/products/${productIdFromRow}/uoms`).then((res) => {
      const product = res.data.data?.product;
      setProductSearch(product ? `${product.sku} — ${product.name}` : '');
      setProductUoms(res.data.data?.uoms || []);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (skipNextProductSearch.current) {
      skipNextProductSearch.current = false;
      return;
    }
    if (!productSearch.trim()) {
      setProductResults([]);
      setShowProductResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search: productSearch, status: 'active', limit: 10 } })
        .then((res) => {
          setProductResults(res.data.data || []);
          setShowProductResults(true);
        })
        .catch(() => setProductResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const handleSelectProduct = (p) => {
    skipNextProductSearch.current = true;
    setProductId(String(p.id));
    setProductSearch(productLabel(p));
    setShowProductResults(false);
    setProductUomId('');
    setCalculated(null);
  };

  // Once a Product is picked, load its own UoM rows for the UoM dropdown.
  useEffect(() => {
    if (!productId) return;
    axios.get(`/api/products/${productId}/uoms`).then((res) => setProductUoms(res.data.data?.uoms || [])).catch(() => setProductUoms([]));
  }, [productId]);

  // Live calculated preview — refreshes whenever the UoM or Rule choice
  // changes. Pre-fills Fixed Amount until staff actually types over it
  // (amountEdited), same "displays calculated until touched" mechanic
  // ProductPricesPage.js uses for a manually-entered Reference Price.
  useEffect(() => {
    if (!productUomId || !ruleId) {
      setCalculated(null);
      return;
    }
    axios.get('/api/supplier-rebate-sku-rates/calculate', { params: { product_uom_id: productUomId, rule_id: ruleId } })
      .then((res) => {
        setCalculated(res.data.data);
        if (!amountEdited && res.data.data.calculatedAmount !== null && res.data.data.calculatedAmount !== undefined) {
          setFixedAmount(res.data.data.calculatedAmount.toFixed(2));
        }
      })
      .catch(() => setCalculated(null));
    // eslint-disable-next-line
  }, [productUomId, ruleId]);

  const selectedRule = rules.find((r) => String(r.id) === String(ruleId));
  const currencyCode = calculated?.currency_code || selectedRule?.supplier?.currency_code;
  const selectedUom = productUoms.find((u) => String(u.id) === String(productUomId));
  const baseUom = productUoms.find((u) => u.is_base);

  const diverges = calculated?.calculatedAmount !== null && calculated?.calculatedAmount !== undefined
    && fixedAmount !== '' && Math.abs(Number(fixedAmount) - calculated.calculatedAmount) > 0.01;

  const handleUseCalculated = () => {
    if (calculated?.calculatedAmount === null || calculated?.calculatedAmount === undefined) return;
    setFixedAmount(calculated.calculatedAmount.toFixed(2));
    setAmountEdited(false);
  };

  const validate = () => {
    if (!productUomId) return 'Select a SKU and Unit of Measure.';
    if (!ruleId) return 'Select which Rule this UoM earns its rebate under.';
    if (fixedAmount === '' || Number.isNaN(Number(fixedAmount))) return 'Fixed Amount is required.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      product_uom_id: productUomId,
      rule_id: ruleId,
      fixed_amount: Number(fixedAmount),
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <label>Product<RequiredMark /></label><br />
          <input
            className="styled-input"
            placeholder="Search name or SKU..."
            value={productSearch}
            disabled={!!initial}
            onChange={(e) => { setProductId(''); setProductSearch(e.target.value); }}
            onFocus={() => { if (productResults.length) setShowProductResults(true); }}
            onBlur={() => setTimeout(() => setShowProductResults(false), 150)}
            style={{ width: 380 }}
          />
          {showProductResults && productResults.length > 0 && (
            <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 380, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
              {productResults.map((p) => (
                <li
                  key={p.id}
                  onMouseDown={() => handleSelectProduct(p)}
                  style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {productLabel(p)}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label>Unit of Measure<RequiredMark /></label><br />
          <select className="styled-input" value={productUomId} onChange={(e) => { setProductUomId(e.target.value); setAmountEdited(false); }} style={{ width: 160 }} disabled={!productId && !initial}>
            <option value="">Select UoM…</option>
            {productUoms.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Rebate Rule<RequiredMark /></label><br />
        <select className="styled-input" value={ruleId} onChange={(e) => { setRuleId(e.target.value); setAmountEdited(false); }} style={{ width: 420 }} disabled={!productId && !initial}>
          <option value="">Select which Rule this UoM earns…</option>
          {rules.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — {r.uom1?.name} → {r.uom2?.name} ({formatPercent(r.rebate_rate, 1)})
            </option>
          ))}
        </select>
        <Link to="/purchasing-supplier-rebate-rules" style={{ marginLeft: 10, color: '#1976d2', fontSize: 13, textDecoration: 'underline' }}>
          Manage rules
        </Link>
      </div>

      {productUomId && ruleId && (
        <div style={{ marginTop: 14, padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #d8dee8' }}>
          <div style={{ fontSize: 13, color: '#666' }}>Optim's calculated figure (from the Rule's formula) — per {baseUom?.name || 'Base Unit'}</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
            {calculated ? formatMoney(calculated.calculatedAmount, currencyCode) : '—'}
          </div>

          {calculated && (
            <table style={{ marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>
                    {selectedRule?.uom1?.name || 'UoM1'} Dealer Price
                    <DealerPriceSource priceAdjustmentId={calculated.uom1_price_adjustment_id} productId={productId} />
                  </td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>{formatMoney(calculated.uom1_dealer_price)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>
                    ÷ {calculated.uom1_conversion_rate} (per {baseUom?.name || 'Base Unit'})
                  </td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>{formatMoney(perBaseUnit(calculated.uom1_dealer_price, calculated.uom1_conversion_rate))}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>
                    {selectedUom?.name || selectedRule?.uom2?.name || 'UoM2'} Dealer Price
                    <DealerPriceSource priceAdjustmentId={calculated.uom2_price_adjustment_id} productId={productId} />
                  </td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>{formatMoney(calculated.uom2_dealer_price)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>
                    ÷ {calculated.uom2_conversion_rate} (per {baseUom?.name || 'Base Unit'})
                  </td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>{formatMoney(perBaseUnit(calculated.uom2_dealer_price, calculated.uom2_conversion_rate))}</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 10px 2px 0', color: '#333', fontWeight: 600, borderTop: '1px solid #eee' }}>Discount Given (per {baseUom?.name || 'Base Unit'})</td>
                  <td style={{ padding: '4px 0 2px', textAlign: 'right', fontWeight: 600, borderTop: '1px solid #eee' }}>{formatMoney(calculated.discountGivenAud)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>Rule: {formatPercent(calculated.rebate_rate, 1)} of Discount Given</td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>{formatMoney(calculated.rebateAud)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 10px 2px 0', color: '#666' }}>÷ Exchange Rate ({formatNumber(calculated.exchange_rate, { decimals: 6 })} {getTenantHomeCurrency()} per {currencyCode})</td>
                  <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>{formatMoney(calculated.calculatedAmount, currencyCode)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {calculated && calculated.calculatedAmount === null && (
            <div style={{ fontSize: 12, color: '#a15c00', marginTop: 4 }}>
              Nothing to compare against yet — either this SKU is missing a UoM1 row, an Adjusted Price at UoM1 or this UoM for the Rule's Customer Price Group.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label>Fixed Amount ({currencyCode || 'Supplier currency'}, per {baseUom?.name || 'Base Unit'})<RequiredMark /></label><br />
        <NumberInput
          decimals={2}
          step="0.01"
          value={fixedAmount}
          onChange={(e) => { setFixedAmount(e.target.value); setAmountEdited(true); }}
          style={{ width: 140 }}
        />
        {calculated?.calculatedAmount !== null && calculated?.calculatedAmount !== undefined && (
          <button type="button" onClick={handleUseCalculated} style={{ marginLeft: 10, padding: '4px 10px', fontSize: 12 }}>
            Use calculated
          </button>
        )}
        {diverges && (
          <div style={{ fontSize: 12, color: '#a15c00', marginTop: 4 }}>
            This overrides Optim's calculated figure ({formatMoney(calculated.calculatedAmount, currencyCode)}) by more than a cent — double check it's not a unit mismatch before saving.
          </div>
        )}
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default SupplierRebateSkuRateForm;
